import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { LocalizedRouterLinkPipe } from '@core/i18n/localized-router-link.pipe';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import {
  RELATORIO_TEMAS,
  RELATORIOS,
  Relatorio,
  RelatorioTema,
} from './relatorios.data';

@Component({
  selector: 'app-relatorios-page',
  standalone: true,
  imports: [LocalizedRouterLinkPipe, PageShellComponent, RouterLink, TranslocoPipe],
  templateUrl: './relatorios.page.html',
})
export class RelatoriosPage {
  private readonly transloco = inject(TranslocoService);

  protected readonly breadcrumb = [{ labelKey: 'nav.items.reports' }];
  protected readonly temas = RELATORIO_TEMAS;
  protected readonly query = signal('');
  protected readonly activeTemas = signal<readonly RelatorioTema[]>([]);
  private readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly sorted = [...RELATORIOS].sort((a, b) =>
    b.publishedAt.localeCompare(a.publishedAt),
  );
  protected readonly lead = this.sorted[0];
  protected readonly isFiltering = computed(
    () => this.query().trim().length > 0 || this.activeTemas().length > 0,
  );
  protected readonly filtered = computed(() => {
    this.activeLanguage();
    const query = this.normalize(this.query());
    const activeTemas = this.activeTemas();

    return this.sorted.filter((relatorio) => {
      if (activeTemas.length > 0 && !activeTemas.includes(relatorio.tema)) return false;
      if (!query) return true;

      return this.normalize(
        [
          this.translate(relatorio.titleKey),
          this.translate(relatorio.descriptionKey),
          this.translate(relatorio.authorsKey),
          this.temaLabel(relatorio.tema),
        ].join(' '),
      ).includes(query);
    });
  });
  protected readonly listReports = computed(() =>
    this.isFiltering()
      ? this.filtered()
      : this.filtered().filter((relatorio) => relatorio.slug !== this.lead?.slug),
  );

  protected updateQuery(event: Event): void {
    this.query.set((event.target as HTMLInputElement).value);
  }

  protected toggleTema(tema: RelatorioTema): void {
    this.activeTemas.update((active) =>
      active.includes(tema) ? active.filter((item) => item !== tema) : [...active, tema],
    );
  }

  protected clearFilters(): void {
    this.query.set('');
    this.activeTemas.set([]);
  }

  protected isTemaActive(tema: RelatorioTema): boolean {
    return this.activeTemas().includes(tema);
  }

  protected temaCount(tema: RelatorioTema): number {
    return this.sorted.filter((relatorio) => relatorio.tema === tema).length;
  }

  protected temaLabel(tema: RelatorioTema): string {
    const option = this.temas.find((item) => item.id === tema);
    return option ? this.translate(option.labelKey) : tema;
  }

  protected translate(key: string): string {
    return this.transloco.translate(key);
  }

  private normalize(value: string): string {
    return value
      .trim()
      .toLocaleLowerCase(this.activeLanguage())
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '');
  }
}
